import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { revokeMySession, revokeOtherSessions } from "../../api/sessions.js";
import type { DeviceSession } from "../../api/sessions.js";
import { ApiClientError } from "../../api/http.js";
import { SESSIONS_KEY, useMySessions } from "../../data/account.js";
import { Alert, Button, Section, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { SessionList } from "./SessionList.js";

/** Where this account is signed in, with sign-out for single devices or all others. */
export function SessionsSection(): ReactNode {
  const sessions = useMySessions();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [revoking, setRevoking] = useState<string | undefined>(undefined);
  const [endingOthers, setEndingOthers] = useState(false);
  const others = (sessions.data ?? []).filter((s: DeviceSession): boolean => !s.current).length;

  async function revoke(session: DeviceSession): Promise<void> {
    setRevoking(session.id);
    try {
      await revokeMySession(session.id);
      toast.notify(`${session.deviceName} abgemeldet.`, { tone: "success" });
    } catch (err: unknown) {
      toast.notify(err instanceof ApiClientError ? err.message : "Abmelden fehlgeschlagen.", { tone: "danger" });
    } finally {
      setRevoking(undefined);
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    }
  }

  async function revokeOthers(): Promise<void> {
    setEndingOthers(true);
    try {
      const ended = await revokeOtherSessions();
      toast.notify(ended === 1 ? "1 Gerät abgemeldet." : `${ended} Geräte abgemeldet.`, { tone: "success" });
    } catch {
      toast.notify("Abmelden fehlgeschlagen.", { tone: "danger" });
    } finally {
      setEndingOthers(false);
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    }
  }

  return (
    <Section
      title="Geräte"
      description="Hier ist dein Konto gerade angemeldet. Melde Geräte ab, die du nicht kennst oder nicht mehr nutzt."
      aside={others > 0 ? (
        <Button size="sm" variant="secondary" icon={LogOut} loading={endingOthers} disabled={revoking !== undefined} onClick={(): void => void revokeOthers()}>
          Alle anderen abmelden
        </Button>
      ) : undefined}
    >
      <div className="flex flex-col gap-3">
        {sessions.isPending ? <Skeleton className="h-16" /> : null}
        {sessions.error !== null ? <Alert tone="danger">{sessions.error.message}</Alert> : null}
        {sessions.data !== undefined ? (
          <SessionList sessions={sessions.data} revoking={revoking} onRevoke={(s: DeviceSession): void => void revoke(s)} empty="Keine aktiven Sitzungen." />
        ) : null}
        <p className="text-[0.8125rem] text-mist-2">
          Ein Gerät, das du nicht kennst? Melde es ab und ändere danach dein Master-Passwort. Bei einer Anmeldung auf einem neuen Gerät bekommst du eine E-Mail.
        </p>
      </div>
    </Section>
  );
}
